/**
 * WB4.4 — Role cockpit defaults layered on WorkbenchIntent (no new screens).
 * Navigator tab · exception source priority · density · inspector storage · action boost.
 */

import type { UserRole } from '@shared/types/roles';
import { ROLES } from '@shared/types/roles';
import type { WorkbenchAction } from './action-types';
import type { WorkbenchDensity } from './density-governance';
import type { WorkbenchException, WorkbenchExceptionSourceId } from './exception-registry';
import { resolveRoleIntentPreset, type RoleIntentPreset } from './intent-presets';

export type NavigatorTabKey = 'queue' | 'exceptions' | 'related';

export interface RoleCockpitDefaults {
  navigatorDefaultTab: NavigatorTabKey;
  prioritizedExceptionSources: readonly WorkbenchExceptionSourceId[];
  density: WorkbenchDensity;
  inspectorDefaultTab: 'flows' | 'advanced';
  aiLayersInInspector: boolean;
}

const FALLBACK_COCKPIT: RoleCockpitDefaults = {
  navigatorDefaultTab: 'queue',
  prioritizedExceptionSources: ['overdue_assignments', 'missing_evidence_tasks', 'credit_hold'],
  density: 'standard',
  inspectorDefaultTab: 'flows',
  aiLayersInInspector: false,
};

/** Per-tenant-role cockpit defaults (static v1 — settings UI defer). */
export const ROLE_COCKPIT_MATRIX: Readonly<Record<UserRole, RoleCockpitDefaults>> = {
  [ROLES.OWNER]: {
    navigatorDefaultTab: 'exceptions',
    prioritizedExceptionSources: ['credit_hold', 'overdue_assignments', 'missing_evidence_tasks'],
    density: 'expert',
    inspectorDefaultTab: 'advanced',
    aiLayersInInspector: true,
  },
  [ROLES.OPERATIONS_MANAGER]: {
    navigatorDefaultTab: 'exceptions',
    prioritizedExceptionSources: ['missing_evidence_tasks', 'overdue_assignments', 'credit_hold'],
    density: 'standard',
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  [ROLES.HR_ADMIN]: {
    navigatorDefaultTab: 'queue',
    prioritizedExceptionSources: ['overdue_assignments', 'missing_evidence_tasks', 'credit_hold'],
    density: 'standard',
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  [ROLES.ACCOUNTING]: {
    navigatorDefaultTab: 'exceptions',
    prioritizedExceptionSources: ['credit_hold', 'overdue_assignments', 'missing_evidence_tasks'],
    density: 'standard',
    inspectorDefaultTab: 'advanced',
    aiLayersInInspector: false,
  },
  [ROLES.SALES]: {
    navigatorDefaultTab: 'queue',
    prioritizedExceptionSources: ['credit_hold', 'overdue_assignments', 'missing_evidence_tasks'],
    density: 'comfort',
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
  [ROLES.EXTERNAL]: {
    navigatorDefaultTab: 'related',
    prioritizedExceptionSources: ['missing_evidence_tasks', 'credit_hold', 'overdue_assignments'],
    density: 'comfort',
    inspectorDefaultTab: 'flows',
    aiLayersInInspector: false,
  },
};

const ROLE_ACTION_ID_BOOST: Readonly<
  Partial<Record<RoleIntentPreset, Readonly<Record<string, number>>>>
> = {
  finance: { export: 12, 'finance:collect': 15, collect: 10 },
  sales: { 'order:create': 15, create: 10, view: 6 },
  warehouse: { 'inventory:receive': 15, receive: 12, 'task:complete': 8 },
};

export function resolveRoleCockpitDefaults(role: UserRole): RoleCockpitDefaults {
  return ROLE_COCKPIT_MATRIX[role] ?? FALLBACK_COCKPIT;
}

const EXCEPTION_SOURCE_RISK_HINTS: Readonly<Record<WorkbenchExceptionSourceId, string>> = {
  credit_hold:
    '예측: 신용 보류가 우선이면 승인 지연 시 고객 이탈·채권 리스크가 증가할 수 있습니다.',
  overdue_assignments:
    '예측: 기한 초과 배정이 우선이면 SLA 초과로 배송·정산 지연이 발생할 수 있습니다.',
  missing_evidence_tasks: '예측: 증빙 누락이 우선이면 감사·컴플라이언스 경고가 발생할 수 있습니다.',
};

export function resolveExceptionSourceRiskHint(sourceId: WorkbenchExceptionSourceId): string {
  return EXCEPTION_SOURCE_RISK_HINTS[sourceId];
}

export function sortExceptionsByRolePriority(
  exceptions: readonly WorkbenchException[],
  role: UserRole
): WorkbenchException[] {
  const priorities = resolveRoleCockpitDefaults(role).prioritizedExceptionSources;
  const rank = new Map(priorities.map((sourceId, index) => [sourceId, index]));

  return [...exceptions].sort((left, right) => {
    const leftRank = rank.get(left.sourceId) ?? priorities.length;
    const rightRank = rank.get(right.sourceId) ?? priorities.length;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return left.title.localeCompare(right.title, 'ko');
  });
}

export function resolveRoleActionBoost(role: UserRole, action: WorkbenchAction): number {
  const preset = resolveRoleIntentPreset(role);
  if (preset === undefined) {
    return 0;
  }

  const idBoosts = ROLE_ACTION_ID_BOOST[preset];
  if (idBoosts === undefined) {
    return 0;
  }

  let boost = 0;
  for (const [fragment, value] of Object.entries(idBoosts)) {
    if (action.id.includes(fragment) || action.handlerRef.includes(fragment)) {
      boost += value;
    }
  }
  return boost;
}
